import type { FC, ReactNode } from 'react';
import ArrowLeftIcon from '@untitled-ui/icons-react/build/esm/ArrowLeft';
import { Box, Container, Stack, SvgIcon, Typography } from '@mui/material';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import { paths } from 'src/paths';
import type { Page as PageType } from 'src/types/page';

interface Section {
  num: string;
  title: string;
  body: ReactNode;
}

const sections: Section[] = [
  {
    num: '01',
    title: 'When refunds may apply',
    body: (
      <>
        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a', mb: 2 }}>
          Refunds are reviewed case by case. They require investigation and explicit owner approval before any payment is reversed.
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 1.5 } }}>
          <Typography component="li" sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a' }}>
            <strong style={{ color: '#0a0a0b' }}>50% deposit (non-refundable).</strong> Once the deposit is paid and we begin reserving production resources, contractor time, software licenses, or stock assets, that deposit is locked in. It is what allows us to commit to the schedule.
          </Typography>
          <Typography component="li" sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a' }}>
            <strong style={{ color: '#0a0a0b' }}>Pre-production refund window.</strong> If active project work has not yet started, you may request a refund within 14 days of your scheduled project start date.
          </Typography>
          <Typography component="li" sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a' }}>
            <strong style={{ color: '#0a0a0b' }}>Incomplete project.</strong> If any portion of the agreed scope is left undelivered by us, you receive a refund covering the uncompleted portion.
          </Typography>
          <Typography component="li" sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a' }}>
            <strong style={{ color: '#0a0a0b' }}>Defective deliverables.</strong> Reported within 7 days of delivery, with supporting evidence.
          </Typography>
          <Typography component="li" sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a' }}>
            <strong style={{ color: '#0a0a0b' }}>Cancellation due to unforeseen circumstances.</strong> See Section 5.
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a', mt: 2.5 }}>
          Completed and accepted deliverables are non-refundable except for proven defects in workmanship. We do not refund for change of mind or shifted preferences once work has begun or third-party costs have been incurred.
        </Typography>
      </>
    )
  },
  {
    num: '02',
    title: 'Request process',
    body: (
      <>
        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a', mb: 2 }}>
          Email or call within 14 days of the issue or project completion. Include your project name, the specific issue, and any supporting files or screenshots.
        </Typography>
        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a', mb: 2 }}>
          We investigate within 5 business days and report back. All approvals require sign-off from the company owner.
        </Typography>
        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a' }}>
          Where applicable consumer-protection laws grant statutory cancellation rights, those rights are honored in accordance with the law.
        </Typography>
      </>
    )
  },
  {
    num: '03',
    title: 'Timeline and method',
    body: (
      <>
        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a', mb: 2 }}>
          Approved refunds are issued within 30 to 45 days, returned via the original payment method or by check.
        </Typography>
        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a' }}>
          Deductions may apply for hours already worked, third-party costs already incurred (software licenses, stock assets, contractor fees), and non-refundable platform charges.
        </Typography>
      </>
    )
  },
  {
    num: '04',
    title: 'Exclusions',
    body: (
      <>
        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a', mb: 2 }}>
          No refunds for misuse of deliverables, third-party costs already paid out on your behalf, or force majeure events.
        </Typography>
        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a', mb: 2 }}>
          This policy applies only to direct engagements with Mystic Media Film. Work delivered through partner agencies or third-party marketplaces is governed by their respective terms.
        </Typography>
        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a' }}>
          All decisions comply with applicable laws. Denials do not waive your right to seek remedies under law.
        </Typography>
      </>
    )
  },
  {
    num: '05',
    title: 'Unforeseen circumstances and changes',
    body: (
      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 17 }, lineHeight: 1.7, color: '#27272a' }}>
        For delays or scope changes caused by staffing shifts, software or platform availability, third-party vendor issues, client-supplied materials, or scope adjustments, we may offer rescheduling, partial refunds, or service credits following investigation and owner approval.
      </Typography>
    )
  }
];

const Page: PageType = () => {
  usePageView();

  return (
    <>
      <Seo
        title="Refund Policy | Mystic Media Film"
        description="Mystic Media Film refund policy. How refunds work for project deposits, retainers, and other Mystic Media Film service agreements."
      />
      <Box component="main" sx={{ background: '#ffffff', minHeight: '100vh' }}>
        <Container maxWidth="md" sx={{ pt: { xs: 14, md: 18 }, pb: { xs: 12, md: 16 } }}>
          {/* Back link */}
          <Box
            component={RouterLink}
            href={paths.index}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: '#52525b',
              textDecoration: 'none',
              fontFamily: '"Roboto Mono", monospace',
              fontSize: 12,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              mb: { xs: 5, md: 7 },
              transition: 'color 0.2s ease',
              '&:hover': { color: '#0a0a0b' }
            }}
          >
            <SvgIcon sx={{ fontSize: 16 }}><ArrowLeftIcon /></SvgIcon>
            Back to home
          </Box>

          {/* Eyebrow pill */}
          <Stack direction="row" sx={{ mb: { xs: 4, md: 5 } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                pl: 0.75,
                pr: 2,
                py: 0.5,
                borderRadius: 999,
                background: '#ffffff',
                border: '1px solid #e4e4e7',
                boxShadow: '0 4px 14px -4px rgba(15, 23, 42, 0.06)'
              }}
            >
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: 12, fontWeight: 700 }}>✶</Box>
              <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: '#0a0a0b' }}>
                Legal · Refund Policy
              </Typography>
            </Stack>
          </Stack>

          {/* Headline */}
          <Typography
            component="h1"
            sx={{
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              fontSize: { xs: 44, md: 64 },
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: '-0.035em',
              color: '#0a0a0b',
              mb: 3
            }}
          >
            Refund Policy.
          </Typography>

          {/* Effective date */}
          <Typography
            sx={{
              fontFamily: '"Roboto Mono", monospace',
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              color: '#71717a',
              mb: { xs: 6, md: 8 }
            }}
          >
            Effective May 16, 2026
          </Typography>

          {/* Intro */}
          <Box sx={{ mb: { xs: 8, md: 10 } }}>
            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 18, md: 20 }, lineHeight: 1.6, color: '#27272a', mb: 2.5, fontWeight: 500 }}>
              Mystic Media Film does not normally provide refunds.
            </Typography>
            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 17, md: 19 }, lineHeight: 1.6, color: '#52525b' }}>
              When they are granted, it follows a case-by-case review and explicit owner approval. By purchasing services from Mystic Media Film, you acknowledge that you have read, understood, and agree to this policy.
            </Typography>
          </Box>

          {/* Sections */}
          {sections.map((s) => (
            <Box key={s.num} sx={{ pt: { xs: 5, md: 6 }, pb: { xs: 1, md: 2 }, borderTop: '1px solid #e4e4e7' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 3, md: 6 }} alignItems="flex-start">
                {/* Section header */}
                <Box sx={{ flexShrink: 0, width: { md: 180 } }}>
                  <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: '#a1a1aa', mb: 1 }}>
                    Section {s.num}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: { xs: 22, md: 24 }, fontWeight: 600, letterSpacing: '-0.015em', color: '#0a0a0b', lineHeight: 1.2 }}>
                    {s.title}
                  </Typography>
                </Box>
                {/* Section body */}
                <Box sx={{ flex: 1, minWidth: 0 }}>{s.body}</Box>
              </Stack>
            </Box>
          ))}

          {/* Contact block */}
          <Box sx={{ mt: { xs: 10, md: 14 }, pt: { xs: 6, md: 8 }, borderTop: '1px solid #e4e4e7' }}>
            <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: '#71717a', mb: 1.5 }}>
              Questions?
            </Typography>
            <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: { xs: 28, md: 36 }, fontWeight: 700, letterSpacing: '-0.025em', color: '#0a0a0b', mb: 3 }}>
              Reach Mystic Media Film.
            </Typography>
            <Stack spacing={1}>
              <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 17, color: '#27272a' }}>
                sales@mysticmediafilm.com
              </Typography>
              <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 17, color: '#27272a' }}>
                (813) 815-0142
              </Typography>
              <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 17, color: '#27272a' }}>
                mysticmediafilm.com
              </Typography>
            </Stack>
          </Box>

          {/* Disclaimer */}
          <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 13, lineHeight: 1.7, color: '#a1a1aa', mt: { xs: 6, md: 8 } }}>
            This document is provided for informational purposes only and is not legal advice. It is subject to applicable Texas and federal laws. Where any provision conflicts with law, the law prevails. Consult a qualified attorney for advice tailored to your situation. Texas consumers may also contact the Office of the Texas Attorney General.
          </Typography>
        </Container>
      </Box>
    </>
  );
};

export default Page;
